import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../AuthContext';
import { API_URL } from '../config/constants';

const AiConsentContext = createContext(null);
const FOCUS_REVALIDATION_DELAY_MS = 200;
export const AI_CONSENT_VERSION = 2;
export const AI_DISCLOSURE_VERSION = '2026-09-06';

const EMPTY_STATE = Object.freeze({
  status: 'missing',
  consentVersion: null,
  requiredConsentVersion: AI_CONSENT_VERSION,
  disclosureVersion: AI_DISCLOSURE_VERSION,
  decidedAt: null,
  source: null,
  processingEnabled: false,
});

export const AiConsentProvider = ({ children }) => {
  const {
    isAuthenticated,
    loading: authLoading,
    authenticatedFetch,
    user,
  } = useAuth();
  const [state, setState] = useState(EMPTY_STATE);
  const [loading, setLoading] = useState(false);
  const [loadedScope, setLoadedScope] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const authenticatedUserId = user?.id || user?.userId || null;
  const scopeKey = isAuthenticated && authenticatedUserId != null
    ? String(authenticatedUserId)
    : null;
  const currentScopeRef = useRef(scopeKey);
  const decisionSequenceRef = useRef(0);
  const inFlightRefreshRef = useRef(null);
  const savingRef = useRef(saving);
  const authRequestRef = useRef(null);
  currentScopeRef.current = scopeKey;
  savingRef.current = saving;
  authRequestRef.current = isAuthenticated && !authLoading ? {
    call: authenticatedFetch,
    scope: scopeKey,
  } : null;

  const isCurrentRequest = useCallback((requestedScope, requestSequence) => (
    currentScopeRef.current === requestedScope
    && decisionSequenceRef.current === requestSequence
  ), []);

  const refresh = useCallback(() => {
    const authRequest = authRequestRef.current;
    const requestedScope = authRequest?.scope;
    const requestSequence = decisionSequenceRef.current;
    if (!requestedScope) {
      return Promise.resolve(null);
    }
    if (
      inFlightRefreshRef.current?.scope === requestedScope
    ) {
      return inFlightRefreshRef.current.promise;
    }
    setLoading(true);

    let requestPromise;
    requestPromise = (async () => {
      try {
        const response = await authRequest.call(`${API_URL}/settings/ai-consent`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Couldn’t load categorization preferences.');
        if (!isCurrentRequest(requestedScope, requestSequence)) return null;
        setError(null);
        setState(data);
        setLoadedScope(requestedScope);
        return data;
      } catch (nextError) {
        if (isCurrentRequest(requestedScope, requestSequence)) {
          setError(nextError.message || 'Couldn’t load categorization preferences.');
        }
        return null;
      } finally {
        if (inFlightRefreshRef.current?.promise === requestPromise) {
          inFlightRefreshRef.current = null;
        }
        if (isCurrentRequest(requestedScope, requestSequence)) {
          setLoading(false);
        }
      }
    })();
    inFlightRefreshRef.current = {
      scope: requestedScope,
      promise: requestPromise,
    };
    return requestPromise;
  }, [isCurrentRequest]);

  useEffect(() => {
    decisionSequenceRef.current += 1;
    inFlightRefreshRef.current = null;
    if (!isAuthenticated || authLoading || !scopeKey) {
      setState(EMPTY_STATE);
      setLoadedScope(null);
      setLoading(false);
      setSaving(false);
      setError(null);
      return;
    }
    setState(EMPTY_STATE);
    setLoadedScope(null);
    setSaving(false);
    setError(null);
    refresh();
    return () => {
      decisionSequenceRef.current += 1;
    };
  }, [authLoading, isAuthenticated, refresh, scopeKey]);

  useEffect(() => {
    if (!isAuthenticated || authLoading || !scopeKey) return undefined;
    let timer = null;
    const scheduleRefresh = () => {
      if (document.visibilityState === 'hidden') return;
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        if (
          currentScopeRef.current === scopeKey
          && !savingRef.current
        ) refresh();
      }, FOCUS_REVALIDATION_DELAY_MS);
    };

    window.addEventListener('focus', scheduleRefresh);
    document.addEventListener('visibilitychange', scheduleRefresh);
    return () => {
      if (timer !== null) window.clearTimeout(timer);
      window.removeEventListener('focus', scheduleRefresh);
      document.removeEventListener('visibilitychange', scheduleRefresh);
    };
  }, [authLoading, isAuthenticated, refresh, scopeKey]);

  const decide = useCallback(async (status) => {
    const requestedScope = scopeKey;
    if (
      !requestedScope
      || loadedScope !== requestedScope
      || error
    ) return null;
    if (status === 'granted' && (state.requiredConsentVersion !== AI_CONSENT_VERSION
      || state.disclosureVersion !== AI_DISCLOSURE_VERSION)) {
      setError('Update Vylo to review the latest AI privacy details.');
      throw new Error('Update Vylo to review the latest AI privacy details.');
    }
    const requestSequence = decisionSequenceRef.current + 1;
    decisionSequenceRef.current = requestSequence;
    inFlightRefreshRef.current = null;
    setSaving(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/settings/ai-consent`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          consentVersion: state.requiredConsentVersion,
          disclosureVersion: state.disclosureVersion,
          presentedDisclosureVersion: AI_DISCLOSURE_VERSION,
          source: 'web',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!isCurrentRequest(requestedScope, requestSequence)) return null;
      if (!response.ok) {
        if (response.status === 409 && data.currentState) {
          setState(data.currentState);
        }
        throw new Error(data.error || 'Couldn’t save categorization preferences.');
      }
      setState(data);
      return data;
    } catch (nextError) {
      if (!isCurrentRequest(requestedScope, requestSequence)) return null;
      const confirmed = await refresh();
      if (confirmed?.status === status) return confirmed;
      throw new Error('Choice not confirmed.');
    } finally {
      if (isCurrentRequest(requestedScope, requestSequence)) setSaving(false);
    }
  }, [authenticatedFetch, error, isCurrentRequest, loadedScope, refresh, scopeKey, state.disclosureVersion, state.requiredConsentVersion]);

  const initialized = Boolean(scopeKey && loadedScope === scopeKey);
  const verified = Boolean(initialized && !error);
  const currentVersionDecided = initialized
    && state.status !== 'missing'
    && state.disclosureVersion === AI_DISCLOSURE_VERSION
    && Number(state.requiredConsentVersion) === AI_CONSENT_VERSION
    && Number(state.consentVersion) === Number(state.requiredConsentVersion);
  const granted = initialized && verified && currentVersionDecided && state.status === 'granted';

  const value = useMemo(() => ({
    state,
    initialized,
    loading,
    saving,
    error,
    granted,
    currentVersionDecided,
    refresh,
    decide,
  }), [
    currentVersionDecided,
    error,
    granted,
    initialized,
    loading,
    refresh,
    saving,
    state,
    decide,
  ]);

  return <AiConsentContext.Provider value={value}>{children}</AiConsentContext.Provider>;
};

export const useAiConsent = () => {
  const context = useContext(AiConsentContext);
  if (!context) throw new Error('useAiConsent must be used within AiConsentProvider');
  return context;
};

// Some low-level providers are rendered independently in unit/fixture harnesses.
// Context absence must be treated as no consent; consumers using this optional
// hook are responsible for an explicit fail-closed default.
export const useOptionalAiConsent = () => useContext(AiConsentContext);

export { EMPTY_STATE as EMPTY_AI_CONSENT_STATE };
